// src/components/Login.tsx
import { yupResolver } from "@hookform/resolvers/yup";
import { EnterIcon } from "@radix-ui/react-icons";
import {
  AlertDialog,
  Avatar,
  Button,
  Card,
  Flex,
  Link,
  Spinner,
  Text,
} from "@radix-ui/themes";
import React, { useState } from "react";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { useMutation } from "react-query";
import { useNavigate } from "react-router-dom";
import TextFieldComponent from "../../components/inputs/Textfield/TextfieldComponent";
import useDecodeToken from "../../hooks/useDecodeToken";
import { routes } from "../../routes";
import { doLogin } from "../../services/dologin/dologin";
import styles from "../../styles/Login.module.scss";
import { APP_VERSION, AUTH_LOCALSTORAGE, BASE_URL, UPLOAD_IMAGES } from "../../utils/constants";
import { formValidation } from "./Login/LoginFormValidation";
import { useFetchManager } from "../../hooks/useFetchManager";
import { TokenResponseType } from "../../types/TokenResponse";
import { jwtDecode } from "jwt-decode";
import { stringToBoolean } from "../../utils/utilities";
import { isEmpty, set } from "lodash";
import { ConfirmAlertDialog } from "../../components/common/ConfirmAlertDialog";
import { EyeOpenIcon, EyeClosedIcon } from "@radix-ui/react-icons";
import { useIES } from "../../hooks/useIES";

const Login: React.FC = () => {
  const navigate = useNavigate();
  const decodeToken = useDecodeToken();
  const manager = useFetchManager();
  const mutation = useMutation({ mutationFn: doLogin, mutationKey: "dologin" });
  const { IES, isLoading } = useIES();
  

  type FormValues = {
    userName: string;
    password: string;
    twoFactorCode?: string;
  };
  const defaultValues: FormValues = {
    userName: "",
    password: "",
  };

  const [userDataWith2FA, setUserDataWith2FA] = useState<FormValues | null>(
    null
  );

  const {
    setValue,
    getValues,
    watch,
    control,
    handleSubmit,
    reset,
    formState: { errors },
    trigger,
  } = useForm<FormValues>({
    defaultValues,
    resolver: yupResolver(formValidation),
    mode: "onBlur",
    reValidateMode: "onBlur",
    shouldUseNativeValidation: false,
  });
  const onSubmit = handleSubmit(async (request) => {
    try {
      const result = await mutation.mutateAsync(request);
      if (result?.token != undefined) {
        const _decodedToken: TokenResponseType = jwtDecode(result?.token);

        if (stringToBoolean(_decodedToken.TwoFactorEnabled)) {
          setValue("twoFactorCode", "");
          setUserDataWith2FA(request);
        } else {
          localStorage.setItem(AUTH_LOCALSTORAGE, result.token);

          decodeToken();
          // setTimeout(fetchManager, 0)
          manager.fetchManager();
          navigate(routes.dashboard);
        }
      }
    } catch (err: any) {
      toast.error(`${mutation.error ?? err?.message}`);
      setValue("twoFactorCode", "");
    }
  });
  const watchTwoFactorCode = watch("twoFactorCode");
  const isEmptyWatchTwoFactorCode = isEmpty(watchTwoFactorCode);
  const confirmAuthWith2FA = async () => {
    const twoFactorCode = getValues("twoFactorCode");

    if (twoFactorCode === "") return;

    const userDataWith2FACode = { ...userDataWith2FA, twoFactorCode };
    setUserDataWith2FA(userDataWith2FACode as FormValues);
    if (userDataWith2FA) {
      try {
        const result = await mutation.mutateAsync(userDataWith2FA);
        if (result?.token != undefined) {
          const _decodedToken: TokenResponseType = jwtDecode(result?.token);

          if (stringToBoolean(_decodedToken.TwoFactorEnabled)) {
            setValue("twoFactorCode", "");
            setUserDataWith2FA(userDataWith2FA);
          } else {
            localStorage.setItem(AUTH_LOCALSTORAGE, result.token);

            decodeToken();
            // setTimeout(fetchManager, 0)
            manager.fetchManager();
            navigate(routes.dashboard);
          }
        }
      } catch (err: any) {
        toast.error(`${mutation.error ?? err?.message}`);
        setValue("twoFactorCode", "");
        setUserDataWith2FA(null);
      }

      setValue("twoFactorCode", "");
    }
  };

  const handleTwoFactorCodeChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const twoFactorCode = e.target.value;
    setValue("twoFactorCode", twoFactorCode);
  };

  const [showPassword, setShowPassword] = useState(false);
  const toggleShowPassword = () => setShowPassword((prev) => !prev);



  return (
    <>
      <div className={styles.loginContainer}>
        <div className={styles.imageSection} style={{
          backgroundImage: `url(${IES.BACKGROUND})`
        }}>
          {/* Coloque aqui a imagem desejada */}
          <img src={`${IES.LOGO}`}
            alt="Login" />
        </div>
        <div className={styles.formSection}>
          <Card
            size="3"
            title="Login"
            variant="classic"
            style={{ width: "50%" }}
          >
            <Flex gap="4" align="center" direction="column">
              <Avatar size="6" fallback="T" radius="none" src={`${IES.LOGO}`} />
              <Text weight={"bold"} as="label">
                Bem-vindo ao ESURA
              </Text>

              <form onSubmit={onSubmit} noValidate>
                <Flex gap="4" direction="column">
                  <TextFieldComponent
                    name="userName"
                    label="Utilizador/Email"
                    control={control}
                    errors={errors}
                    fieldProps={{ placeholder: "Digite o Utilizador/Email" }}
                  />
                  <div style={{ position: "relative", width: "100%" }}>
                    <TextFieldComponent
                      name="password"
                      label="Senha"
                      control={control}
                      errors={errors}
                      fieldProps={{
                        placeholder: "Digite sua senha",
                        type: showPassword ? "text" : "password",
                      }}
                      style={{ width: "100%" }}
                      required={true}
                    />

                    <button
                      type="button"
                      onClick={toggleShowPassword}
                      style={{
                        position: "absolute",
                        right: "10px",
                        top: "40px",
                        background: "none",
                        border: "none",
                        padding: 0,
                        cursor: "pointer",
                      }}
                    >
                      {showPassword ? <EyeOpenIcon /> : <EyeClosedIcon />}
                    </button>
                  </div>

                  <Link
                    size="1"
                    onClick={() => navigate(routes.forgotPassword)}
                  >
                    Esqueceu a senha?
                  </Link>

                  <Button
                    variant="surface"
                    type="submit"
                    disabled={mutation.isLoading}
                  >
                    {mutation.isLoading ? (
                      <>
                        {" "}
                        <Spinner loading>
                          {" "}
                          <EnterIcon />{" "}
                        </Spinner>{" "}
                      </>
                    ) : (
                      <EnterIcon />
                    )}{" "}
                    Entrar
                  </Button>
                </Flex>
              </form>
              
              {/* Versão da Aplicação */}
              <Text size="1" color="gray" style={{ marginTop: "1rem" }}>
                v{APP_VERSION}
              </Text>
            </Flex>
          </Card>
        </div>
      </div>
      <ConfirmAlertDialog
        open={!!userDataWith2FA}
        descriptionComponent={
          <>
            <TextFieldComponent
              name="twoFactorCode"
              label="Código OTP"
              control={control}
              errors={errors}
              fieldProps={{
                placeholder: "Código OTP",
                handleTwoFactorCodeChange,
              }}
              style={{ width: "100%" }}
              required={true}
            />
          </>
        }
        onOpenChange={(open) => !open && setUserDataWith2FA(null)}
        title="Confirmar o código de 2FA"
        item={userDataWith2FA}
        disabledConfirm={isEmptyWatchTwoFactorCode}
        onConfirm={confirmAuthWith2FA}
        onCancel={() => setUserDataWith2FA(null)}
      />
    </>
  );
};

export default Login;